import type { Deal, Specs } from './types';

export function won(n: number | null | undefined): string {
  if (n == null) return '-';
  return String(Math.round(n)).replace(/\B(?=(\d{3})+(?!\d))/g, ',') + '원';
}

export function price(d: Pick<Deal, 'price_krw' | 'price_foreign' | 'currency' | 'effective_price'>): string {
  if (d.effective_price != null) return won(d.effective_price);
  if (d.price_krw != null) return won(d.price_krw);
  if (d.price_foreign != null) return `${d.currency === 'USD' ? '$' : d.currency + ' '}${d.price_foreign}`;
  return '가격 미확인';
}

export function timeAgo(iso: string | null | undefined, now: Date = new Date()): string {
  if (!iso) return '';
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return '';
  const m = Math.max(0, Math.round((now.getTime() - t) / 60000));
  if (m < 1) return '방금';
  if (m < 60) return `${m}분 전`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}시간 전`;
  const d = Math.round(h / 24);
  return d < 7 ? `${d}일 전` : iso.slice(5, 10).replace('-', '.');
}

export function clock(d: Date | null): string {
  if (!d) return '';
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

export function dayLabel(d: Date | null, now: Date = new Date()): string {
  if (!d) return '오늘';
  const day = (x: Date) => `${x.getFullYear()}-${x.getMonth()}-${x.getDate()}`;
  if (day(d) === day(now)) return '오늘';
  const y = new Date(now);
  y.setDate(now.getDate() - 1);
  if (day(d) === day(y)) return '어제';
  return `${d.getMonth() + 1}월 ${d.getDate()}일`;
}

const NO_SHIP_MALLS = new Set(['스팀', '에픽', '닌텐도', 'GOG', '유비소프트', '플레이스테이션', 'PSN', '엑스박스']);

export function shipText(d: Pick<Deal, 'shipping_krw' | 'mall' | 'category'>): string {
  if (d.shipping_krw === 0) return NO_SHIP_MALLS.has(d.mall ?? '') ? '배송 없음' : '무료배송';
  if (d.shipping_krw == null) return NO_SHIP_MALLS.has(d.mall ?? '') ? '배송 없음' : '배송 미확인';
  return `배송비 ${won(d.shipping_krw)}`;
}

/** 시장가 비교 한 줄. tone 은 색만 결정한다. */
export function marketLine(d: Deal): { text: string; tone: 'good' | 'muted' } {
  if (d.market_price && d.effective_price != null && d.market_price > 0) {
    const pct = Math.round((1 - d.effective_price / d.market_price) * 100);
    if (pct > 0) return { text: `시장가보다 ${pct}% 저렴`, tone: 'good' };
    if (pct < 0) return { text: `시장가보다 ${-pct}% 비쌈`, tone: 'muted' };
    return { text: '시장가와 같음', tone: 'muted' };
  }
  return { text: '시장가 미확인', tone: 'muted' };
}

/** 평균가 대비 한 줄. 관측 일수가 3일 이상일 때만 (그 전엔 평균이라 부를 수 없다). */
export function averageLine(d: Deal): string | null {
  const days = d.market_days ?? 0;
  if (!d.market_avg_90d || days < 3 || d.effective_price == null) return null;
  const pct = Math.round((1 - d.effective_price / d.market_avg_90d) * 100);
  const cmp = pct > 0 ? `${pct}% 저렴` : pct < 0 ? `${-pct}% 비쌈` : '같음';
  return `${days}일 평균 시장가 ${won(d.market_avg_90d)}보다 ${cmp}`;
}

export function specText(s: Specs | null | undefined): string {
  if (!s) return '';
  const parts: string[] = [];
  if (s.gpu) parts.push(s.gpu);
  if (s.cpu) parts.push(s.cpu);
  if (s.ram_gb) parts.push(`${s.ram_gb}GB`);
  if (s.ssd_gb) parts.push(s.ssd_gb >= 1000 ? `${Math.round(s.ssd_gb / 1000)}TB` : `${s.ssd_gb}GB`);
  if (s.screen_inch) parts.push(`${s.screen_inch}인치`);
  if (s.refresh_hz) parts.push(`${s.refresh_hz}Hz`);
  return parts.join(' · ');
}

const SOURCE_NAMES: Record<string, string> = {
  ruliweb: '루리웹', ppomppu: '뽐뿌', clien: '클리앙', quasarzone: '퀘이사존', dealbada: '딜바다', bbasak: '빠삭',
  zod: '조드', eomisae: '어미새', navercafe: '네이버카페', toss: '토스',
};

export function sourceNames(sources: string[]): string {
  return sources.map((s) => SOURCE_NAMES[s] ?? s).join(' · ');
}

export type Group = '전체' | '디지털·PC' | '게임' | '식품' | '패션' | '생활';
export const GROUPS: Group[] = ['전체', '디지털·PC', '게임', '식품', '패션', '생활'];

/** 소스마다 분류 이름이 달라서(게시판 분류, 카페 이름) 몇 개 묶음으로만 나눈다.
 *  상품명과 게시판 분류를 함께 보되, 구체적인 말(식품·패션)을 먼저 본다.
 *  "틴케이스", "파워에이드" 처럼 부품 이름이 부분 문자열로 들어간 상품을 디지털로 오해하지 않기 위해서다. */
const GAME = /게임|스팀|에픽|닌텐도|플레이스테이션|플스|ps[45]\b|xbox|switch|스위치\s?2|dlc|타이틀|콘솔/;
const FOOD = /식품|음료|먹거리|과자|쿠키|빵|라면|국수|우동|짜장|짬뽕|교자|만두|김밥|떡|커피|우유|두유|고기|한우|삼겹|닭가슴살|생수|김치|과일|채소|계란|유정란|간식|초콜|아이스크림|치킨|피자|햄|참치|쌀|햇반|견과|젤리|사탕|주스|탄산|맥주|소주|와인|건강기능|비타민|유산균|영양제|홍삼|반찬|양념|소스|참기름|건어물|오징어|새우|연어|굴비|젓갈|선물세트/;
const FASHION = /패션|의류|잡화|신발|운동화|스니커즈|자켓|재킷|점퍼|코트|바지|팬츠|티셔츠|맨투맨|후드|니트|가디건|원피스|스커트|양말|모자|가방|백팩|지갑|브라|팬티|내의|파자마|잠옷|구두|샌들|슬리퍼/;
const DIGITAL = /\b(rtx|rx\s?\d{3,}|ssd|hdd|nvme|ddr\d?|cpu|gpu|ram|oled|qled)\b|라이젠|인텔|i[3579][-\s]?\d{4}|노트북|데스크탑|조립pc|모니터|키보드|마우스|그래픽카드|메인보드|파워서플라이|pc\s?케이스|컴퓨터|태블릿|스마트폰|갤럭시|아이폰|아이패드|맥북|에어팟|버즈|이어폰|헤드폰|헤드셋|스피커|충전기|보조배터리|공유기|\btv\b|가전|청소기|에어컨|냉장고|세탁기|건조기|공기청정기|드라이기|면도기|전자레인지|밥솥|카메라|프린터/;

export function groupOf(d: Deal): Group {
  const hay = `${d.category ?? ''} ${d.product}`.toLowerCase();
  if (GAME.test(hay) || NO_SHIP_MALLS.has(d.mall ?? '')) return '게임';
  if (FOOD.test(hay)) return '식품';
  if (FASHION.test(hay)) return '패션';
  if (DIGITAL.test(hay) || /pc|디지털|a\/v|전자/.test((d.category ?? '').toLowerCase())) return '디지털·PC';
  return '생활';
}


// 판매 페이지 링크를 못 찾았을 때 판매처의 검색 결과로라도 보낸다. 커뮤니티 글로 보내는 것보다 구매에 가깝다.
const MALL_SEARCH: Record<string, string> = {
  쿠팡: 'https://www.coupang.com/np/search?q=',
  G마켓: 'https://browse.gmarket.co.kr/search?keyword=',
  옥션: 'https://browse.auction.co.kr/search?keyword=',
  '11번가': 'https://search.11st.co.kr/Search.tmall?kwd=',
  네이버: 'https://search.shopping.naver.com/search/all?query=',
  롯데온: 'https://www.lotteon.com/search/search/search.ecn?render=search&platform=pc&q=',
  SSG: 'https://www.ssg.com/search.ssg?query=',
  카카오: 'https://store.kakao.com/search/result?q=',
  무신사: 'https://www.musinsa.com/search/goods?keyword=',
  올리브영: 'https://www.oliveyoung.co.kr/store/search/getSearchMain.do?query=',
  컬리: 'https://www.kurly.com/search?sword=',
  스팀: 'https://store.steampowered.com/search/?term=',
  알리: 'https://ko.aliexpress.com/wholesale?SearchText=',
  다나와: 'https://search.danawa.com/dsearch.php?query=',
};

export function mallSearchUrl(d: Pick<Deal, 'mall' | 'product'>): { url: string; label: string } {
  const q = encodeURIComponent(d.product.slice(0, 60));
  if (d.mall && MALL_SEARCH[d.mall]) return { url: MALL_SEARCH[d.mall] + q, label: `${d.mall}에서 찾기` };
  return { url: MALL_SEARCH['다나와'] + q, label: '최저가 찾기 (다나와)' };
}

/**
 * 조건 spec 을 사람이 읽는 말로 푼다. `parsed` 문자열은 수집기가 만든 개발자용이라 화면에 쓰지 않는다.
 * products 는 "이 중 하나라도 제목에 있으면" 이고, rules 는 가격·배송 같은 나머지 조건이다.
 */
export function summarize(spec: Record<string, unknown> | undefined): { products: string[]; rules: string[] } {
  const arr = (v: unknown) => (Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : []);
  const any = arr(spec?.any_keywords);
  const all = arr(spec?.keywords);
  const rules: string[] = [];
  const max = spec?.max_price;
  const min = spec?.min_price;
  if (typeof max === 'number') rules.push(`${max.toLocaleString('ko-KR')}원 이하`);
  if (typeof min === 'number') rules.push(`${min.toLocaleString('ko-KR')}원 이상`);
  if (spec?.free_shipping === true) rules.push('무료배송만');
  for (const m of arr(spec?.malls)) rules.push(`${m}에서만`);
  for (const e of arr(spec?.exclude)) rules.push(`${e} 제외`);
  const sp = (spec?.specs ?? {}) as Record<string, unknown>;
  if (typeof sp.gpu_min === 'string') rules.push(`${sp.gpu_min} 이상`);
  if (typeof sp.ram_gb_min === 'number') rules.push(`RAM ${sp.ram_gb_min}GB 이상`);
  if (typeof sp.ssd_gb_min === 'number') rules.push(sp.ssd_gb_min >= 1000 ? `저장 ${sp.ssd_gb_min / 1000}TB 이상` : `저장 ${sp.ssd_gb_min}GB 이상`);
  return { products: any.length ? any : all, rules };
}
